export default function fastSnap(sec1Ref, sec2Ref, wrapperRef, opts = {}) {
  const getEl = (ref) => (ref && ref.current) || null;
  const vh = () =>
    window.visualViewport?.height ||
    window.innerHeight ||
    document.documentElement.clientHeight;

  const headerAllowancePx = opts.headerAllowancePx ?? 80;
  const edgeSlackPx = opts.edgeSlackPx ?? 6;
  const snapBackThreshold = opts.snapBackThreshold ?? 0.92;

  const heroExclusiveOnScreen = () => {
    const w = getEl(wrapperRef);
    if (!w) return true;
    const r = w.getBoundingClientRect();
    const topInside = r.top <= headerAllowancePx + edgeSlackPx;
    const bottomInside = r.bottom >= vh() - edgeSlackPx;
    return topInside && bottomInside;
  };

  const pageY = () => window.scrollY || window.pageYOffset || 0;

  // Custom smooth scroll — Safari's native smooth scroll is slower than Chrome's.
  const scrollToSection = (ref) => {
    const el = getEl(ref);
    if (!el) return;
    const startY = pageY();
    const targetY = startY + el.getBoundingClientRect().top;
    const dist = targetY - startY;
    const duration = 420; // ms — consistent across browsers
    const startTime = performance.now();

    const ease = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t); // ease-in-out quad

    const step = (now) => {
      const elapsed = Math.min((now - startTime) / duration, 1);
      window.scrollTo(0, startY + dist * ease(elapsed));
      if (elapsed < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  let active = 1;
  let cooldown = false;

  let io;
  const s1 = getEl(sec1Ref);
  const s2 = getEl(sec2Ref);

  let s2Ratio = 0;

  if ("IntersectionObserver" in window && s1 && s2) {
    io = new IntersectionObserver(
      (entries) => {
        if (!heroExclusiveOnScreen()) return;

        for (const entry of entries) {
          if (entry.target === s2) {
            s2Ratio = entry.intersectionRatio;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
              active = 2;
            } else if (entry.intersectionRatio <= 0.45) {
              active = 1;
            }
          }
          if (entry.target === s1) {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
              active = 1;
            }
          }
        }
      },
      { threshold: [0.0, 0.45, 0.55, 1.0] },
    );
    io.observe(s1);
    io.observe(s2);
  } else {
    const updateActive = () => {
      if (!heroExclusiveOnScreen()) return;
      const a = getEl(sec1Ref),
        b = getEl(sec2Ref);
      if (!a || !b) return;

      const H = vh();
      const r1 = a.getBoundingClientRect();
      const r2 = b.getBoundingClientRect();

      const vis1 =
        Math.max(0, Math.min(r1.bottom, H) - Math.max(r1.top, 0)) / H;
      const vis2 =
        Math.max(0, Math.min(r2.bottom, H) - Math.max(r2.top, 0)) / H;

      s2Ratio = vis2;
      active = vis2 > vis1 ? 2 : 1;
    };
    updateActive();
    window.addEventListener("scroll", updateActive, { passive: true });
  }

  const armCooldown = () => {
    cooldown = true;
    setTimeout(() => (cooldown = false), 500);
  };

  const wheelThreshold = opts.wheelThreshold ?? 50;
  let wheelAccumulator = 0;

  const onWheel = (e) => {
    if (cooldown) {
      // If cooling down, prevent scroll but don't accumulate
      if (heroExclusiveOnScreen()) e.preventDefault();
      return;
    }
    if (!heroExclusiveOnScreen()) return;

    // Allow scrolling OUT of the hero section
    if ((active === 2 && e.deltaY > 0) || (active === 1 && e.deltaY < 0)) {
      wheelAccumulator = 0;
      return;
    }

    // Always prevent default to keep the hero locked in place
    // until we decide to switch sections.
    e.preventDefault();

    wheelAccumulator += e.deltaY;

    if (active === 1 && wheelAccumulator > wheelThreshold) {
      scrollToSection(sec2Ref);
      armCooldown();
      wheelAccumulator = 0;
    } else if (active === 2 && wheelAccumulator < -wheelThreshold) {
      if (s2Ratio >= snapBackThreshold) {
        scrollToSection(sec1Ref);
        armCooldown();
        wheelAccumulator = 0;
      }
    } else {
      // Reset accumulator if direction changes or if effectively idle
      if (
        (active === 1 && wheelAccumulator < 0) ||
        (active === 2 && wheelAccumulator > 0)
      ) {
        wheelAccumulator = 0;
      }
    }
  };

  let startY = null;
  let startT = 0;

  const onTouchStart = (e) => {
    startY = e.touches[0].clientY;
    startT = performance.now();
  };

  const onTouchEnd = (e) => {
    if (startY == null || cooldown) return;
    if (!heroExclusiveOnScreen()) {
      startY = null;
      return;
    }

    const dy = startY - e.changedTouches[0].clientY;
    const dt = performance.now() - startT;
    const MIN_DIST = 40,
      MAX_DT = 500;
    const isGesture = Math.abs(dy) >= MIN_DIST || dt <= MAX_DT;
    if (!isGesture) {
      startY = null;
      return;
    }

    if (active === 1 && dy > 0) {
      scrollToSection(sec2Ref);
      armCooldown();
    } else if (active === 2 && dy < 0) {
      if (s2Ratio >= snapBackThreshold) {
        scrollToSection(sec1Ref);
        armCooldown();
      }
    }
    startY = null;
  };

  document.addEventListener("wheel", onWheel, { passive: false });
  document.addEventListener("touchstart", onTouchStart, { passive: true });
  document.addEventListener("touchend", onTouchEnd, { passive: true });

  return () => {
    document.removeEventListener("wheel", onWheel);
    document.removeEventListener("touchstart", onTouchStart);
    document.removeEventListener("touchend", onTouchEnd);
    if (io) io.disconnect();
  };
}
